import { useCallback, useEffect, useRef } from 'react';

import { GENERATION_CONTENT_PREFIX } from 'apps/web/constants/localStorage';
import { GenerationContent } from 'apps/web/services/recentGenerationStorage';
import { useRouter } from 'next/router';

import useLocalStorageItem from '../useLocalStorageItem';

import { sanitize } from '../../utils/sanitize';

interface UseGenerationLoaderProps {
  setResponseToShow: (content: string) => void;
}

const isGenerationContent = (obj: unknown): obj is GenerationContent => {
  return (
    obj !== null &&
    typeof obj === 'object' &&
    'id' in obj &&
    'content' in obj &&
    'generatedAt' in obj &&
    typeof (obj as GenerationContent).id === 'string' &&
    typeof (obj as GenerationContent).generatedAt === 'number' &&
    (obj as GenerationContent).content !== null &&
    (obj as GenerationContent).content !== undefined
  );
};

export function useGenerationLoader({
  setResponseToShow,
}: UseGenerationLoaderProps) {
  const router = useRouter();
  const { getItemByKey } = useLocalStorageItem();
  const { id: generationId } = router?.query as { id: string };
  const inputsLoadedRef = useRef(false);

  const getGeneration = useCallback((): GenerationContent | null => {
    if (!generationId) {
      return null;
    }
    const sanitizedGenerationId = sanitize(generationId);
    const generation = getItemByKey(
      `${GENERATION_CONTENT_PREFIX}${sanitizedGenerationId}`,
    );
    return isGenerationContent(generation) ? generation : null;
  }, [generationId, getItemByKey]);

  const loadGeneration = useCallback(() => {
    const generation = getGeneration();

    if (isGenerationContent(generation)) {
      if (generation.content) {
        const content =
          typeof generation.content === 'string'
            ? generation.content
            : JSON.stringify(generation.content);
        setResponseToShow(content);
      }
    }
  }, [getGeneration, setResponseToShow]);

  const loadInputs = useCallback(
    (callback?: () => void) => {
      const generation = getGeneration();

      if (isGenerationContent(generation) && generation.inputs) {
        requestAnimationFrame(() => {
          Object.entries(generation.inputs).forEach(
            ([inputKey, inputValue]) => {
              if (inputValue !== undefined && inputValue !== null) {
                const valueToStore =
                  typeof inputValue === 'object'
                    ? JSON.stringify(inputValue)
                    : String(inputValue);
                try {
                  localStorage.setItem(inputKey, valueToStore);
                } catch (error) {
                  console.warn(
                    'Failed to set input value in localStorage:',
                    error,
                  );
                }
              }
            },
          );

          inputsLoadedRef.current = true;
          if (callback) callback();
        });
      }
    },
    [getGeneration],
  );

  useEffect(() => {
    inputsLoadedRef.current = false;
  }, [generationId]);

  useEffect(() => {
    loadGeneration();
    loadInputs();
  }, [loadGeneration, loadInputs]);
}
